import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

const CycleAnimation = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const pathVariants = {
    hidden: { pathLength: 0, opacity: 0 },
    visible: {
      pathLength: 1,
      opacity: 1,
      transition: {
        duration: 2,
        ease: "easeInOut",
      },
    },
  };

  const iconVariants = {
    hidden: { scale: 0, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  const steps = [
    { icon: "🐟", title: "Poisson", description: "Produit des déchets naturels" },
    { icon: "🔄", title: "Filtration", description: "Conversion en nutriments" },
    { icon: "🌱", title: "Plantes", description: "Absorption des nutriments" },
    { icon: "💧", title: "Eau pure", description: "Retour vers les poissons" },
  ];

  return (
    <section className="py-20 bg-white relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-emerald-50/50" />
      
      <div className="relative max-w-6xl mx-auto px-6">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Le cycle naturel en action
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Découvrez comment notre système crée un écosystème parfait
          </p>
        </motion.div>

        <div className="relative">
          {/* Desktop Cycle */}
          <div className="hidden md:block">
            <svg
              className="w-full h-96 mb-12"
              viewBox="0 0 800 400"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* Cycle Path */}
              <motion.path
                variants={pathVariants}
                initial="hidden"
                animate={inView ? "visible" : "hidden"}
                d="M 200 200 Q 400 100 600 200 Q 400 300 200 200"
                fill="none"
                stroke="url(#gradient)"
                strokeWidth="4"
                strokeDasharray="10,5"
              />
              
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#10b981" />
                  <stop offset="50%" stopColor="#3b82f6" />
                  <stop offset="100%" stopColor="#10b981" />
                </linearGradient>
              </defs>
            </svg>

            {/* Step Icons */}
            <div className="grid grid-cols-4 gap-8 relative -mt-20">
              {steps.map((step, index) => (
                <motion.div
                  key={index}
                  variants={iconVariants}
                  initial="hidden"
                  animate={inView ? "visible" : "hidden"}
                  transition={{ delay: index * 0.2 }}
                  className="text-center"
                >
                  <div className="w-20 h-20 bg-white rounded-full shadow-2xl flex items-center justify-center text-3xl mb-4 mx-auto border-4 border-emerald-200 hover:border-emerald-400 transition-all duration-300">
                    {step.icon}
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-gray-600">{step.description}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Mobile Cycle */}
          <div className="md:hidden space-y-8">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -30 }}
                animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
                transition={{ delay: index * 0.2, duration: 0.8 }}
                className="flex items-center space-x-4 p-6 bg-white rounded-3xl shadow-xl border border-gray-100"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-blue-400 rounded-full flex items-center justify-center text-2xl text-white shadow-lg">
                  {step.icon}
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-lg mb-1">{step.title}</h3>
                  <p className="text-gray-600 text-sm">{step.description}</p>
                </div>
                {index < steps.length - 1 && (
                  <div className="absolute right-6 top-full w-0.5 h-8 bg-gradient-to-b from-emerald-400 to-blue-400 transform translate-y-4" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CycleAnimation;